// src/pages/TestHistoryPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUserTests } from "./Utility/dataService";



function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function scoreColor(pct) {
  // returns Tailwind classes, keeping it clean on white
  if (pct >= 85) return "bg-emerald-600";
  if (pct >= 60) return "bg-blue-600";
  if (pct >= 40) return "bg-amber-500";
  return "bg-rose-600";
}


function getInitials(title) {
  const words = (title || "")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return "T";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function TestHistoryPage() {
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest"); // newest | best | worst | title

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const rows = await fetchUserTests();
        if (!cancelled) setTests(rows);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load tests");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);



  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    let arr = tests.filter((t) => {
      if (!q) return true;
      return (
        (t.title || "").toLowerCase().includes(q) ||
        String(t.score).includes(q) ||
        String(t.totalQuestions).includes(q)
      );
    });

    arr = [...arr].sort((a, b) => {
      const ap = a.totalQuestions ? (a.score / a.totalQuestions) * 100 : 0;
      const bp = b.totalQuestions ? (b.score / b.totalQuestions) * 100 : 0;

      if (sort === "newest") return new Date(b.createdAt) - new Date(a.createdAt);
      if (sort === "best") return bp - ap;
      if (sort === "worst") return ap - bp;
      if (sort === "title") return (a.title || "").localeCompare(b.title || "");
      return 0;
    });

    return arr;
  }, [tests, query, sort]); // ✅ include tests


  const summary = useMemo(() => {
    const total = tests.length;
    const totalQuestions = tests.reduce((s, t) => s + (t.totalQuestions || 0), 0);
    const totalScore = tests.reduce((s, t) => s + (t.score || 0), 0);
    const pct = totalQuestions ? Math.round((totalScore / totalQuestions) * 100) : 0;
    return { total, totalQuestions, totalScore, pct };
  }, [tests]); // ✅ not []

  const onOpenTest = (testId) => {
    // Change this route to whatever your app uses
    navigate('/result', {
      state: {
        testId: testId
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white/85 backdrop-blur border-b border-gray-100">
        <div className="mx-auto w-full max-w-5xl px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                Test History
              </h1>
              <p className="text-sm text-gray-500">
                Tap any test to view full attempt history.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="rounded-full px-3 py-1 text-sm ring-1 ring-inset ring-gray-200 text-gray-700 bg-gray-50">
                {summary.total} tests
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title or score…"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900
                           outline-none focus:ring-2 focus:ring-gray-200"
              />
              {query ? (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                >
                  Clear
                </button>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none
                           focus:ring-2 focus:ring-gray-200"
              >
                <option value="newest">Newest</option>
                {/* <option value="best">Best score</option>
                <option value="worst">Worst score</option> */}
                <option value="title">Title A–Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <div className="mx-auto mb-2 h-10 w-10 rounded-2xl bg-gray-100" />
            <h2 className="text-base font-semibold text-gray-900">No matches</h2>
            <p className="mt-1 text-sm text-gray-500">
              Try a different keyword (e.g., “fractions” or “20”).
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {filtered.map((t) => {
              const pctRaw = t.totalQuestions ? (t.score / t.totalQuestions) * 100 : 0;
              const pct = clamp(Math.round(pctRaw), 0, 100);
              const barClass = scoreColor(pct);

              return (
                <button
                  key={t.id}
                  onClick={() => onOpenTest(t.id)}
                  className="group text-left rounded-2xl border border-gray-200 bg-white p-4 shadow-sm
                             hover:shadow-md hover:-translate-y-[1px] transition
                             focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar-style badge */}
                    <div className="h-12 w-12 shrink-0 rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200 flex items-center justify-center">
                      <span className="text-sm font-semibold text-gray-700">
                        {getInitials(t.title)}
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      {/* Title */}
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="truncate text-base font-semibold text-gray-900">
                          {t.title}
                        </h3>

                        {/* Small “open” hint (tweet-card vibe) */}
                        <span className="shrink-0 rounded-full px-2 py-1 text-xs text-gray-600 bg-gray-50 ring-1 ring-inset ring-gray-200 opacity-0 group-hover:opacity-100 transition">
                          View →
                        </span>
                      </div>

                      {/* Score line */}
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-gray-500 truncate">
                          {formatDate(t.createdAt)}
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3 h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full ${barClass} rounded-full transition-all`}
                          style={{ width: `${0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
